import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';

import ConfirmDialog from 'components/confirm-dialog';
import { useCart } from 'hooks/use-cart';
import { t } from 'state/intl/translations';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import noop from 'utils/noop';

interface IDialogCallback {
  onConfirm?: Function;
  onCancel?: Function;
}

interface IUseSwitchCartMethodDialog {
  SwitchCartMethodDialog: FC;
  openSwitchCartMethod: (callbacks?: IDialogCallback) => void;
  shouldShowSwitchToStoreDialog: boolean;
  shouldShowSwitchToMobileDialog: boolean;
}

const useSwitchCartMethodDialog = (): IUseSwitchCartMethodDialog => {
  const {
    resetInRestaurantRedemption,
    inRestaurantRedemptionCart,
    inRestaurantRedemptionEnabled,
    enableOfferRedemption,
    enableRewardRedemption,
  } = useInRestaurantRedemptionContext();
  const { emptyCart, isCartEmpty } = useCart();
  const { cartEntries: minimalCartEntries, emptyMinimalCart } = useMinimalCart();

  const isInRestaurantRedemptionEmpty = !inRestaurantRedemptionCart?.length;

  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;

  const dialogCallbackRef = useRef<
    { onConfirm?: Function; onCancel?: Function } | null | undefined
  >(null);
  const dialogCopyRef = useRef({
    heading: t('changeRedemptionToInStore'),
    body: t('changeRedemptionToInStoreDescription'),
    confirmLabel: t('changeRedemptionToInStoreConfirmLabel'),
  });

  // The cart-reset dispatches (`resetInRestaurantRedemption` / `emptyCart` / `emptyMinimalCart`)
  // and the consumer-provided `onConfirm` callback both trigger broad Redux state changes that
  // force large render cascades on Android. Running them synchronously here would batch them
  // into the same React commit as `setOpen(false)`, preventing the dialog from painting closed
  // until the whole cascade finishes (~2s of frozen dialog on Android). Instead, commit only
  // `setOpen(false)` synchronously, then defer the heavy work to the next animation frame so
  // the dialog-close paint happens first.
  const onConfirmSwitchCartMethodDialog = useCallback(() => {
    const onConfirm = dialogCallbackRef.current?.onConfirm;
    dialogCallbackRef.current = null;
    setOpen(false);

    requestAnimationFrame(() => {
      if (!isInRestaurantRedemptionEmpty) {
        resetInRestaurantRedemption();
      } else if (!isCartEmpty) {
        emptyCart();
      } else if (minimalCartEntries.length > 0) {
        emptyMinimalCart();
      }

      if (typeof onConfirm === 'function') {
        onConfirm();
      }
    });
  }, [
    isInRestaurantRedemptionEmpty,
    isCartEmpty,
    minimalCartEntries.length,
    resetInRestaurantRedemption,
    emptyCart,
    emptyMinimalCart,
  ]);

  const onCancelSwitchCartMethodDialog = useCallback(() => {
    if (typeof dialogCallbackRef.current?.onCancel === 'function') {
      dialogCallbackRef.current.onCancel();
    }

    dialogCallbackRef.current = null;
    setOpen(false);
  }, []);

  // should allow switching store if there is item in cart and brand enables offer or reward redemption
  const shouldShowSwitchToStoreDialog = Boolean(
    inRestaurantRedemptionEnabled &&
      (!isCartEmpty || minimalCartEntries.length > 0) &&
      (enableOfferRedemption || enableRewardRedemption)
  );
  const shouldShowSwitchToMobileDialog = Boolean(
    inRestaurantRedemptionEnabled && !isInRestaurantRedemptionEmpty
  );

  const confirmFnRef = useRef(onConfirmSwitchCartMethodDialog);
  confirmFnRef.current = onConfirmSwitchCartMethodDialog;
  const cancelFnRef = useRef(onCancelSwitchCartMethodDialog);
  cancelFnRef.current = onCancelSwitchCartMethodDialog;

  const switchCartMethodDialogRef = useRef<FC | null>(null);
  if (!switchCartMethodDialogRef.current) {
    switchCartMethodDialogRef.current = function SwitchCartMethodDialog() {
      const dialogCopy = dialogCopyRef.current;

      return (
        <ConfirmDialog
          showDialog={openRef.current}
          heading={dialogCopy.heading}
          body={dialogCopy.body}
          confirmLabel={dialogCopy.confirmLabel}
          cancelLabel={t('noThankYou')}
          onConfirm={() => confirmFnRef.current()}
          onCancel={() => cancelFnRef.current()}
          onDismiss={noop}
          modalAppearanceEventMessage="Switching redemption method"
        />
      );
    };
  }
  const SwitchCartMethodDialog = switchCartMethodDialogRef.current;

  const openSwitchCartMethod = useCallback(
    (callbacks?: IDialogCallback) => {
      dialogCallbackRef.current = callbacks;
      dialogCopyRef.current = isInRestaurantRedemptionEmpty
        ? {
            heading: t('changeRedemptionToInStore'),
            body: t('changeRedemptionToInStoreDescription'),
            confirmLabel: t('changeRedemptionToInStoreConfirmLabel'),
          }
        : {
            heading: t('changeRedemptionToPickUp'),
            body: t('changeRedemptionToPickUpDescription'),
            confirmLabel: t('changeRedemptionToPickUpConfirmLabel'),
          };
      setOpen(true);
    },
    [isInRestaurantRedemptionEmpty]
  );

  return useMemo(
    () => ({
      SwitchCartMethodDialog,
      openSwitchCartMethod,
      shouldShowSwitchToStoreDialog,
      shouldShowSwitchToMobileDialog,
    }),
    [
      SwitchCartMethodDialog,
      openSwitchCartMethod,
      shouldShowSwitchToStoreDialog,
      shouldShowSwitchToMobileDialog,
    ]
  );
};

export default useSwitchCartMethodDialog;
